import { ENDPOINTS } from 'config/api';
import fetch from 'app/utilities/fetch';
import { NEWS } from 'config/news';
import { switchMap } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';

/*
    How a response joins what the store already holds. The list is a contiguous
    run of pages rather than a single one, so a page arriving at either end is
    stitched on instead of replacing everything: that is what lets the carousel
    keep the slides it has, and so what stops it flashing and jumping.
*/
export const MODE = {
    APPEND: 'append',
    PREPEND: 'prepend',
    REPLACE: 'replace'
};

export const INITIAL_STATE = {
    category: null, // null means no filter, i.e. every category
    // Whether the articles the category features are left out of these. Part of
    // which listing they are rather than a detail of how they were asked for:
    // the same category answers with a different set of articles either way, so
    // a run of pages cannot be joined across a change of it.
    excludeFeatured: false,
    items: undefined,
    isBusy: false,
    // Whether `items` belong to a request that has since been replaced. They are
    // left in place so the section does not empty out mid navigation, and this
    // is what tells the carousel they are on their way out.
    isStale: false,
    // The run of pages `items` covers, inclusive
    firstPage: 1,
    lastPage: 1,
    resultCount: undefined
};

// Actions
export const FETCH_FILTERED_NEWS_FAIL = 'rfa-zoo-website/filtered-news/FETCH_FILTERED_NEWS_FAIL';
export const FETCH_FILTERED_NEWS_SUCCESS = 'rfa-zoo-website/filtered-news/FETCH_FILTERED_NEWS_SUCCESS';
export const FETCH_FILTERED_NEWS = 'rfa-zoo-website/filtered-news/FETCH_FILTERED_NEWS';


// Action Creators
// `pages` is a list because backfilling the run before the page someone landed
// on takes more than one request, and they have to land as a single prepend
// The two layouts page at different sizes, so the size travels with the request
// rather than being read from config by the epic
export const fetchFilteredNewsAction = (category = null, pages = [1], mode = MODE.REPLACE, limit = NEWS.FILTER_LIMIT, excludeFeatured = false) => ({
    type: FETCH_FILTERED_NEWS,
    payload: {
        category: category,
        excludeFeatured: excludeFeatured,
        isBusy: true,
        limit: limit,
        mode: mode,
        pages: pages
    }
});

export const fetchFilteredNewsFailAction = (category, pages, mode, excludeFeatured = false) => ({
    type: FETCH_FILTERED_NEWS_FAIL,
    payload: {
        category: category,
        excludeFeatured: excludeFeatured,
        isBusy: false,
        mode: mode,
        pages: pages
    }
});

export const fetchFilteredNewsSuccessAction = ({ items, resultCount }, category, pages = [1], mode = MODE.REPLACE, excludeFeatured = false) => ({
    type: FETCH_FILTERED_NEWS_SUCCESS,
    payload: {
        category: category,
        excludeFeatured: excludeFeatured,
        items: items,
        isBusy: false,
        mode: mode,
        pages: pages,
        resultCount: resultCount
    }
});


// Reducers
export default (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case FETCH_FILTERED_NEWS:
            return fetchFilteredNews(state, action.payload);
        case FETCH_FILTERED_NEWS_FAIL:
            return fetchFilteredNewsFail(state, action.payload);
        case FETCH_FILTERED_NEWS_SUCCESS:
            return fetchFilteredNewsSuccess(state, action.payload);
        default:
            return state;
    }
};

function fetchFilteredNews(state, { category, excludeFeatured, isBusy, mode, pages }) { // eslint-disable-line require-jsdoc
    const isJoining = mode !== MODE.REPLACE &&
        state.category === category &&
        state.excludeFeatured === Boolean(excludeFeatured) &&
        Array.isArray(state.items);

    return {
        ...state,
        category: category,
        // Held as a boolean whatever it arrived as, so that comparing one
        // listing against another is a comparison of two of the same thing
        excludeFeatured: Boolean(excludeFeatured),
        isBusy: isBusy,
        // Kept whatever is being fetched, so a change of filter or category
        // leaves the articles on screen until their replacements arrive rather
        // than emptying the section out and snapping it shut in between. The
        // loader is only for having nothing to show at all, which is a first
        // load. Ones being replaced are marked instead, and shown as leaving.
        items: state.items,
        isStale: !isJoining && Array.isArray(state.items),
        firstPage: isJoining ? state.firstPage : pages[0],
        lastPage: isJoining ? state.lastPage : pages[pages.length - 1]
    };
}

function fetchFilteredNewsFail(state, { category, excludeFeatured, isBusy }) { // eslint-disable-line require-jsdoc
    return {
        ...state,
        category: category,
        excludeFeatured: Boolean(excludeFeatured),
        // Articles held over from the request that failed belong to a different
        // listing, so leaving them up would caption them as this one
        items: state.isStale ? [] : state.items || [],
        isBusy: isBusy,
        isStale: false
    };
}

function fetchFilteredNewsSuccess(state, { category, excludeFeatured, items, mode, pages, resultCount }) { // eslint-disable-line require-jsdoc
    const first = pages[0];
    const last = pages[pages.length - 1];
    const canJoin = state.category === category &&
        state.excludeFeatured === Boolean(excludeFeatured) &&
        Array.isArray(state.items);

    // Joining is only ever onto articles of the same listing, so anything held
    // over from a request that has been replaced has to be dropped rather than
    // added to, which `isStale` is what marks
    if (canJoin && !state.isStale && mode === MODE.APPEND) {
        return { ...state, isBusy: false, items: [...state.items, ...items], lastPage: last, resultCount: resultCount };
    }

    if (canJoin && !state.isStale && mode === MODE.PREPEND) {
        return { ...state, isBusy: false, items: [...items, ...state.items], firstPage: first, resultCount: resultCount };
    }

    return {
        ...state,
        category: category,
        excludeFeatured: Boolean(excludeFeatured),
        isBusy: false,
        isStale: false,
        items: items,
        firstPage: first,
        lastPage: last,
        resultCount: resultCount
    };
}


// Epic creator
/**
 * Filtered News Epic
 * @param  {func} endpoint                      - News endpoint
 * @param  {func} formatNews                    - Format response function
 * @param  {func} fetchFilteredNewsSuccessAction - Success action creator
 * @param  {func} fetchFilteredNewsFailAction    - Fail action creator
 * @return {func}
 */
export const createFilteredNewsEpic = (endpoint, formatNews, fetchFilteredNewsSuccessAction, fetchFilteredNewsFailAction) => {
    return (action$) => action$.pipe(
        ofType(FETCH_FILTERED_NEWS),
        // switchMap so a quick change of filter or page discards the earlier
        // response instead of letting it land after the newer one
        switchMap(({ payload }) => {
            const { category, excludeFeatured, limit, mode, pages } = payload;

            // In page order, so a multi page backfill reads as one run of
            // articles rather than being interleaved by whichever replied first
            return (
                Promise.all(pages.map((page) => fetch(endpoint(category, limit, page, excludeFeatured))))
                    .then((responses) => {
                        const merged = responses.reduce((all, response) => {
                            const { items, resultCount } = formatNews(response);

                            return { items: [...all.items, ...items], resultCount: resultCount || all.resultCount };
                        }, { items: [], resultCount: undefined });

                        return fetchFilteredNewsSuccessAction(merged, category, pages, mode, excludeFeatured);
                    })
                    .catch(() => {
                        // TODO: Add proper error handling
                        return fetchFilteredNewsFailAction(category, pages, mode, excludeFeatured);
                    })
            );
        })
    );
};


// Epics
const itemsEpic = createFilteredNewsEpic(
    ENDPOINTS.NEWS_FILTERED,
    formatNews,
    fetchFilteredNewsSuccessAction,
    fetchFilteredNewsFailAction
);


/**
 * Formats the API response
 * @param  {object} response – Raw API response
 * @return {object}          - Formatted API response
 */
export function formatNews(response) {
    if (response && response.data && response.data.length) {
        const { data, meta } = response;

        // Format "attributes" to "data" for EntityLink and the tiles.
        // The endpoint returns the full article body for every item, which is
        // the bulk of the payload and is not used by a card, so it is dropped
        // rather than shipped in the server rendered state.
        const items = data.map(({ attributes, ...item }) => {
            const { blocks, ...cardAttributes } = attributes || {}; // eslint-disable-line no-unused-vars

            return {
                ...item,
                attributes: cardAttributes,
                data: cardAttributes
            };
        });

        return {
            items: items,
            resultCount: (meta || {}).result_count
        };
    }

    return { items: [], resultCount: 0 };
}

export const epics = combineEpics(itemsEpic);
